import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:3001/api' : '/api');

const ACCESS_TOKEN_KEY = 'meradio_access_token';
const REFRESH_TOKEN_KEY = 'meradio_refresh_token';

export const tokenStorage = {
  getAccessToken: (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),
  setTokens: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  clearTokens: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
  hasTokens: (): boolean => {
    return !!(tokenStorage.getAccessToken() && tokenStorage.getRefreshToken());
  }
};

interface ExtendedInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  skipErrorToast?: boolean;
}

export interface APIResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
  errors?: Array<{
    field: string;
    message: string;
    value?: any;
  }>;
}

const createAPIClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const accessToken = tokenStorage.getAccessToken();
      
      if (accessToken && config.headers) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as ExtendedInternalAxiosRequestConfig;

      if (error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after'];
        const retryAfterSeconds = retryAfter ? parseInt(retryAfter, 10) : 60;

        const errorData = error.response.data as APIResponse;
        const message = errorData.message || `Too many requests. Please try again in ${retryAfterSeconds} seconds.`;

        if (!(originalRequest as ExtendedInternalAxiosRequestConfig)?.skipErrorToast) {
          console.error(message);
        }

        (error as any).retryAfter = retryAfterSeconds;
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        const refreshToken = tokenStorage.getRefreshToken();
        
        if (refreshToken) {
          try {
            const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`, {
              refreshToken
            });

            if (refreshResponse.data.success) {
              const { accessToken, refreshToken: newRefreshToken } = refreshResponse.data.data.tokens;

              tokenStorage.setTokens(accessToken, newRefreshToken);

              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              }

              return client(originalRequest);
            }
          } catch (refreshError) {
            tokenStorage.clearTokens();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        } else {
          tokenStorage.clearTokens();
          window.location.href = '/login';
        }
      }

      if (error.response?.data) {
        const errorData = error.response.data as APIResponse;

        if (errorData.message && !(originalRequest as ExtendedInternalAxiosRequestConfig)?.skipErrorToast) {
          console.error(errorData.message);
        }
      } else if (error.code === 'ERR_CANCELED' || axios.isCancel(error)) {
        return Promise.reject(error);
      } else if (error.code === 'ECONNABORTED') {
        console.error('Request timeout. Please try again.');
      } else if (!navigator.onLine) {
        console.error('No internet connection. Please check your network.');
      } else {
        console.error('Something went wrong. Please try again.');
      }

      return Promise.reject(error);
    }
  );

  return client;
};

export const apiClient = createAPIClient();

export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> =>
    apiClient.get(url, config).then(response => response.data),
    
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> =>
    apiClient.post(url, data, config).then(response => response.data),
    
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> =>
    apiClient.put(url, data, config).then(response => response.data),
    
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> =>
    apiClient.delete(url, config).then(response => response.data),
};

export const isAuthenticated = (): boolean => {
  return tokenStorage.hasTokens();
};

export const logout = (): void => {
  tokenStorage.clearTokens();
  window.location.href = '/login';
};
